/* eslint-disable bsky-internal/avoid-unwrapped-text */
import {type ReactNode, useEffect} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'

import {useAgent} from '#/state/session'
import {Button, ButtonText} from '#/components/Button'
import {BodyText} from '#/wsocial/components/primitives'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import widLogoSvg from '../../../assets/images/onboarding/widentity_logo.svg'
import {MobileTopNav, PageShell, SupportFooter} from './primitives'
import {openWIdentityDeepLink, useQrDeepLink} from './useQrDeepLink'

export function StepCard({
  logo,
  title,
  body,
  buttonLabel,
  onPress,
  disabled,
  testID,
}: {
  logo?: ReactNode
  title: ReactNode
  body: ReactNode
  buttonLabel: ReactNode
  onPress: () => void
  disabled?: boolean
  testID?: string
}) {
  const tokens = useSemanticTokens()
  return (
    <div
      style={{
        border: `1px solid ${tokens.onboarding.btnPrimaryBg}`,
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 24,
        padding: '20px 16px',
      }}>
      {logo && <div style={{flexShrink: 0}}>{logo}</div>}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          width: '100%',
          textAlign: 'center',
        }}>
        <p
          style={{
            fontSize: 30,
            fontWeight: 700,
            lineHeight: '35px',
            color: tokens.onboarding.textPrimary,
            margin: 0,
            width: '100%',
          }}>
          {title}
        </p>
        <BodyText style={{textAlign: 'center', width: '100%'}}>{body}</BodyText>
      </div>
      <Button
        label="Open W Identity"
        color="primary"
        size="large"
        testID={testID}
        onPress={onPress}
        disabled={disabled}>
        <ButtonText>{buttonLabel}</ButtonText>
      </Button>
    </div>
  )
}

export function StepWidSetupMobile({
  onBack,
  onSkip,
}: {
  onBack: () => void
  onSkip?: () => void
}) {
  const agent = useAgent()
  const queryClient = useQueryClient()

  useEffect(() => {
    return () => {
      void queryClient.cancelQueries({queryKey: ['wid-setup-mobile']})
      queryClient.removeQueries({queryKey: ['wid-setup-mobile']})
    }
  }, [queryClient])

  const {data: session} = useQuery({
    queryKey: ['wid-setup-mobile'],
    queryFn: async ({signal}) =>
      agent.quicklogin.allocateWidForAccount({signal}),
  })

  const setupDeepLink = useQrDeepLink(session?.qrCodeUrl)

  return (
    <PageShell>
      <MobileTopNav onBack={onBack} />
      <div style={{padding: '40px 16px 0'}}>
        <StepCard
          logo={
            <img
              src={widLogoSvg}
              alt="W Identity"
              style={{width: 73, height: 73}}
            />
          }
          title="Verify your account"
          body="Complete the setup in W Identity, then use the button below."
          buttonLabel="Verify with W Identity"
          onPress={() => openWIdentityDeepLink(setupDeepLink)}
          disabled={!setupDeepLink}
          testID="setupWidBtn"
        />
      </div>

      {onSkip && (
        <div
          style={{
            padding: '32px 16px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}>
          <Button
            label="Explore W without verifying"
            color="secondary"
            size="large"
            testID="skipBtn"
            onPress={onSkip}
            style={{width: '100%'}}>
            <ButtonText>Explore W without verifying</ButtonText>
          </Button>
        </div>
      )}

      <SupportFooter />
    </PageShell>
  )
}
